import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDevice } from '../../context/DeviceContext';
import { toast } from '../Toast';
import ChevronIcon from '../ChevronIcon';
import {
  readPanelOrder,
  SENSITIVITY_PANEL_ORDER_KEY,
  writePanelOrder,
} from '../../utils/layoutPreferences';
import {
  CUSTOM_REPORT_RATE_RANGE,
  clampCustomReportRateHz,
  pollingRateTableToHz,
} from '../../utils/PollingRates';
import { isLegacyDpiColorModel } from '../../utils/DeviceCatalog';
import {
  AUTO_APPLY_STATUS_TEXT,
  useAutoApply,
} from '../../utils/useAutoApply';
import './Sensitivity.css';

const STORAGE_KEY = 'mouse-driver.sensitivity.customPresets';
const RATE_OPTIONS = [125, 250, 500, 1000, 2000, 4000, 8000];
const DEFAULT_COLORS = [
  [136, 136, 136],
  [76, 175, 80],
  [33, 150, 243],
  [255, 193, 7],
  [244, 67, 54],
  [233, 30, 99],
  [156, 39, 176],
];
const LEVEL_DEFAULT_DPI = [400, 800, 1600, 3200, 6400, 12800, 26000];
const THEME2_DPI_SHORTCUTS = [400, 800, 1600, 3200, 4800, 6400];
/** 滑条进度用中性色，与灯光色解耦 */
const SLIDER_TRACK = '#3b82f6';
const PALETTE = [
  [255, 255, 255], [255, 0, 0], [255, 128, 0], [255, 255, 0],
  [0, 255, 0], [0, 255, 255], [0, 128, 255], [0, 0, 255],
  [128, 0, 255], [255, 0, 255], [233, 30, 99], [76, 175, 80],
  [33, 150, 243], [255, 193, 7], [128, 128, 128], [0, 0, 0],
];
/** 每个回报率档位默认灯光色（与 Hz 一一对应） */
const DEFAULT_RATE_COLORS = {
  125: [136, 136, 136],
  250: [76, 175, 80],
  500: [33, 150, 243],
  1000: [0, 200, 255],
  2000: [255, 193, 7],
  4000: [244, 67, 54],
  8000: [156, 39, 176],
};

const makeRateColorMap = (seed) => {
  const map = {};
  RATE_OPTIONS.forEach((hz, i) => {
    const fromSeed = seed?.[hz] || seed?.[String(hz)];
    map[hz] = Array.isArray(fromSeed)
      ? [...fromSeed]
      : [...(DEFAULT_RATE_COLORS[hz] || DEFAULT_COLORS[i] || DEFAULT_COLORS[0])];
  });
  return map;
};

const rgbToHex = (c) => {
  const [r, g, b] = Array.isArray(c) ? c : [59, 130, 246];
  return `#${[r, g, b].map((v) => clampByte(v).toString(16).padStart(2, '0')).join('')}`;
};
const hexToRgb = (hex) => {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return [59, 130, 246];
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};
const clampByte = (v) => Math.max(0, Math.min(255, Number(v) | 0));
const type3HasRate = (hz) => Number(hz) > 0;

const FEATURED_PRESETS = [
  // 默认设置：进入灵敏度页时默认选中
  {
    id: 'f-default',
    name: '默认设置',
    levels: [400, 800, 1600, 3200, 6400],
    wirelessRate: 1000,
    wiredRate: 1000,
  },
  { id: 'f-cs2', name: 'CS 2', levels: [800], wirelessRate: 2000, wiredRate: 2000 },
  { id: 'f-apex', name: 'Apex', levels: [1600], wirelessRate: 4000, wiredRate: 4000 },
  { id: 'f-fortnite', name: 'Fortnite', levels: [1600], wirelessRate: 4000, wiredRate: 4000 },
  { id: 'f-valorant', name: 'Valorant', levels: [800], wirelessRate: 2000, wiredRate: 2000 },
  { id: 'f-rivals', name: 'Marvel Rivals', levels: [1600], wirelessRate: 4000, wiredRate: 4000 },
  { id: 'f-ow', name: 'Overwatch', levels: [1600], wirelessRate: 4000, wiredRate: 4000 },
  { id: 'f-pubg', name: 'PUBG', levels: [800], wirelessRate: 2000, wiredRate: 2000 },
  { id: 'f-cod', name: 'Call of Duty', levels: [1600], wirelessRate: 4000, wiredRate: 4000 },
  { id: 'f-bf', name: 'Battlefield', levels: [1600], wirelessRate: 4000, wiredRate: 4000 },
  { id: 'f-dota', name: 'DOTA2', levels: [1600], wirelessRate: 4000, wiredRate: 4000 },
  { id: 'f-lol', name: 'LOL', levels: [1600], wirelessRate: 4000, wiredRate: 4000 },
].map((p) => ({
  ...p,
  type: 'featured',
  currentLevel: Math.min(2, (p.levels.length >= 5 ? p.levels : [400, 800, p.levels[0] || 1600, 3200, 6400]).length - 1),
  independentXY: false,
  wirelessRateColors: makeRateColorMap(),
  wiredRateColors: makeRateColorMap(),
  levels: (p.levels.length >= 5 ? p.levels : [400, 800, p.levels[0] || 1600, 3200, 6400]).map((dpi, i) => ({
    dpi,
    dpiY: dpi,
    color: DEFAULT_COLORS[i] || DEFAULT_COLORS[0],
    defaultDpi: LEVEL_DEFAULT_DPI[i] || dpi,
  })),
}));

const DEFAULT_FEATURED = FEATURED_PRESETS.find((p) => p.id === 'f-default') || FEATURED_PRESETS[0];

const uid = () => `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
const clampDpi = (v) => Math.max(100, Math.min(30000, Number(v) || 100));
const DEFAULT_LEVEL_DPI = [400, 800, 1600, 3200, 6400];
/** 档位 Y 轴 DPI；未开启独立 X/Y 时与 X 相同 */
const levelDpiY = (lv) => clampDpi(Number(lv?.dpiY) > 0 ? lv.dpiY : lv?.dpi);
/** 设备/预设里的一档可能是数字（X=Y）或 {x, y} */
const toDpiPair = (entry, fallback) => {
  const isPair = entry && typeof entry === 'object';
  const rawX = isPair ? entry.x ?? entry.dpi : entry;
  const rawY = isPair ? entry.y ?? entry.dpiY : entry;
  const x = clampDpi(Number(rawX) > 0 ? rawX : fallback);
  return { x, y: Number(rawY) > 0 ? clampDpi(rawY) : x };
};
const THEME2_SLIDER_MAX = 1000;
const dpiToTheme2Slider = (dpi) =>
  Math.sqrt((clampDpi(dpi) - 100) / (30000 - 100)) * THEME2_SLIDER_MAX;
const theme2SliderToDpi = (position) => {
  const ratio = Math.max(0, Math.min(1, Number(position) / THEME2_SLIDER_MAX));
  return clampDpi(Math.round((100 + ratio * ratio * (30000 - 100)) / 50) * 50);
};
const rgbCss = (c) => (Array.isArray(c) ? `rgb(${c[0]}, ${c[1]}, ${c[2]})` : c || '#3b82f6');

function loadCustomPresets() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const list = JSON.parse(raw);
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function saveCustomPresets(list) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
}

function snapshotFromDevice(
  dpiValues,
  colors,
  currentLevel,
  wirelessRate,
  wiredRate,
  rateColorMaps,
  rateCustomFlags
) {
  const levels = (dpiValues?.length ? dpiValues : DEFAULT_LEVEL_DPI).map((entry, i) => {
    const { x, y } = toDpiPair(entry, DEFAULT_LEVEL_DPI[i] || 1600);
    return {
      dpi: x,
      dpiY: y,
      color: colors?.[i] || DEFAULT_COLORS[i] || DEFAULT_COLORS[0],
      defaultDpi: LEVEL_DEFAULT_DPI[i] || x,
    };
  });
  // 有线 / 无线必须独立；缺省各自落到 1000，互不回退覆盖
  const wireless = Number(wirelessRate) > 0 ? Number(wirelessRate) : 1000;
  const wired = Number(wiredRate) > 0 ? Number(wiredRate) : 1000;
  return {
    id: 'device-current',
    name: '板载灵敏度',
    type: 'device',
    levels,
    // 设备任一档位 X≠Y 时，直接以独立模式呈现
    independentXY: levels.some((lv) => lv.dpiY !== lv.dpi),
    currentLevel: currentLevel ?? Math.min(2, levels.length - 1),
    wirelessRate: wireless,
    wiredRate: wired,
    wirelessRateCustom: !!rateCustomFlags?.wireless,
    wiredRateCustom: !!rateCustomFlags?.wired,
    // 每个 Hz 档位各自灯光色（协议确认前先存预设/本地）
    wirelessRateColors: makeRateColorMap(rateColorMaps?.wireless),
    wiredRateColors: makeRateColorMap(rateColorMaps?.wired),
  };
}

/** 兼容旧预设字段 wirelessColor/wiredColor → 按档位表；旧预设没有 dpiY */
function normalizePreset(preset) {
  if (!preset) return preset;
  const next = { ...preset };
  const levels = (Array.isArray(next.levels) ? next.levels : []).map((lv) => {
    const { x, y } = toDpiPair(lv, LEVEL_DEFAULT_DPI[0]);
    return { ...lv, dpi: x, dpiY: y };
  });
  const independentXY =
    typeof next.independentXY === 'boolean'
      ? next.independentXY
      : levels.some((lv) => lv.dpiY !== lv.dpi);
  next.independentXY = independentXY;
  next.levels = independentXY ? levels : levels.map((lv) => ({ ...lv, dpiY: lv.dpi }));
  // 有线 / 无线独立，不再互相回退
  next.wirelessRate = Number(next.wirelessRate) > 0 ? Number(next.wirelessRate) : 1000;
  next.wiredRate = Number(next.wiredRate) > 0 ? Number(next.wiredRate) : 1000;
  next.wirelessRateCustom = !!next.wirelessRateCustom;
  next.wiredRateCustom = !!next.wiredRateCustom;
  next.wirelessRateColors = makeRateColorMap(
    next.wirelessRateColors ||
      (next.wirelessColor
        ? Object.fromEntries(RATE_OPTIONS.map((hz) => [hz, next.wirelessColor]))
        : null)
  );
  next.wiredRateColors = makeRateColorMap(
    next.wiredRateColors ||
      (next.wiredColor
        ? Object.fromEntries(RATE_OPTIONS.map((hz) => [hz, next.wiredColor]))
        : null)
  );
  delete next.wirelessColor;
  delete next.wiredColor;
  return next;
}

export default function Sensitivity({ theme = 'theme1' }) {
  const { sender, dataStore, isConnected, deviceInfo } = useDevice();
  const usesSharedReportRate = !dataStore?.isProtocolAtLeast(4, 0);
  const [isPresetListRight, setIsPresetListRight] = useState(
    () =>
      readPanelOrder(SENSITIVITY_PANEL_ORDER_KEY, 'list-left') ===
      'list-right'
  );
  const [search, setSearch] = useState('');
  const [featuredOpen, setFeaturedOpen] = useState(true);
  const [customOpen, setCustomOpen] = useState(true);
  const [customPresets, setCustomPresets] = useState(() =>
    loadCustomPresets().map(normalizePreset)
  );
  const [selectedId, setSelectedId] = useState('device-current');
  const [draft, setDraft] = useState(() =>
    snapshotFromDevice()
  );
  const [deviceSnap, setDeviceSnap] = useState(() => snapshotFromDevice());
  const [supportedRates, setSupportedRates] = useState({
    wired: [],
    wireless: [],
  });
  const [menuLevel, setMenuLevel] = useState(null);
  /** theme2 独立 X/Y 时，滑条当前编辑的轴 */
  const [theme2Axis, setTheme2Axis] = useState('x');
  const [presetMenu, setPresetMenu] = useState(null);
  // colorTarget: { type:'level'|'rate', index?, rateKey?:'wireless'|'wired', hz?:number }
  const [colorTarget, setColorTarget] = useState(null);
  const [tempColor, setTempColor] = useState([255, 255, 255]);
  const [renaming, setRenaming] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [presetActionLock, setPresetActionLock] = useState(false);
  const menuRef = useRef(null);
  const presetMenuRef = useRef(null);
  const colorPopRef = useRef(null);
  const nameRef = useRef(null);
  // 同步锁：防止快速连点在 setState 生效前重复触发
  const presetActionLockRef = useRef(false);

  const isCustom = draft?.type === 'custom';
  const isFeatured = draft?.type === 'featured';
  const isDevice = draft?.type === 'device' || draft?.id === 'device-current';

  /**
   * 把一份配置（DPI 整表 + 当前档位 + 回报率）写入设备。
   * busy / toast 由调用方处理，手动应用与自动应用共用。
   */
  const writePresetToDevice = async (targetPreset) => {
    if (!targetPreset?.levels) return;
    if (!isConnected || !sender || !dataStore) {
      throw new Error('设备未连接');
    }
    const { type1, type3 } = await ensurePerformanceRaw();

    const level = Math.min(
      Math.max(0, targetPreset.currentLevel ?? 0),
      Math.max(0, (targetPreset.levels?.length || 1) - 1)
    );
    const dpiValues = (targetPreset.levels || []).map((lv) => ({
      x: clampDpi(lv.dpi),
      y: targetPreset.independentXY ? levelDpiY(lv) : clampDpi(lv.dpi),
    }));

    // 一次写入整表 DPI + 当前档位，避免每档 setDPI 触发多次 write/confirm 导致 fail to write
    const tableResult = await sender.setDPITable(dpiValues, type1, { currentLevel: level });
    if (tableResult?.raw && dataStore.data?.performanceData) {
      const prev = dataStore.data.performanceData.type1;
      const newParsed = prev?.parsed ? { ...prev.parsed } : null;
      if (newParsed?.dpiTable) {
        newParsed.dpiTable = newParsed.dpiTable.map((row, i) =>
          i < dpiValues.length
            ? { ...row, x: dpiValues[i].x, y: dpiValues[i].y }
            : row
        );
        newParsed.dpiCurrentLevel = level;
      }
      dataStore.data.performanceData.type1 = {
        raw: tableResult.raw,
        parsed: newParsed || prev?.parsed || null,
        timestamp: Date.now(),
      };
    }
    await sender.wait(60);

    const targetRates = usesSharedReportRate
      ? { wiredRate: targetPreset.wiredRate || targetPreset.wirelessRate || 1000 }
      : {
          wiredRate: targetPreset.wiredRate || 1000,
          wirelessRate: targetPreset.wirelessRate || 1000,
          wiredCustom: !!targetPreset.wiredRateCustom,
          wirelessCustom: !!targetPreset.wirelessRateCustom,
        };
    await sender.setPollingRates(
      targetRates,
      dataStore.getData('performanceData')?.type3?.raw || type3
    );
    await sender.wait(80);
    await sender.getPerformanceData(1);
    await sender.wait(50);
    await sender.getPerformanceData(3);
    await sender.wait(50);
    await sender.getInformationData();

    if (targetPreset.type === 'custom') {
      setCustomPresets((list) =>
        list.map((p) => (p.id === targetPreset.id ? { ...targetPreset } : p))
      );
    }
  };

  // 「当前设备」草稿的 DPI 表编辑自动写入设备；预设编辑仍走手动应用按钮。
  // 回报率 / 档位切换 / 档位灯色本来就是改动即写，不经过这里。
  const { saveStatus, markDirty, ownedByUser } = useAutoApply({
    value: draft,
    apply: writePresetToDevice,
    enabled: isConnected && !!sender && !!dataStore && isDevice,
    isConnected,
    onError: (err) => toast.error(`应用失败: ${err.message || '写入设备失败'}`),
  });

  useEffect(() => {
    saveCustomPresets(customPresets);
  }, [customPresets]);

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuLevel(null);
      if (
        presetMenuRef.current &&
        !presetMenuRef.current.contains(e.target) &&
        !e.target.closest?.('.sens-preset-more')
      ) {
        setPresetMenu(null);
      }
      if (colorPopRef.current && !colorPopRef.current.contains(e.target)) {
        // 色点本身会 stopPropagation；点外部关闭
        if (!e.target.closest?.('.sens-color-swatch')) setColorTarget(null);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (renaming && nameRef.current) {
      nameRef.current.focus();
      nameRef.current.select();
    }
  }, [renaming]);

  const applyDeviceData = useCallback((info, perf, color) => {
    const dpiTable = perf?.type1?.parsed?.dpiTable;
    const dpiValues = Array.isArray(dpiTable)
      ? dpiTable.filter((d) => Number(d?.x) > 0).map((d) => ({ x: d.x, y: d.y }))
      : null;
    const colors =
      !dataStore?.isProtocolAtLeast(4, 0) && isLegacyDpiColorModel(deviceInfo)
        ? color?.parsed?.dpiRGBTable || perf?.type1?.parsed?.dpiRGBTable
        : perf?.type1?.parsed?.dpiRGBTable;
    const level =
      perf?.type1?.parsed?.dpiCurrentLevel ?? info?.parsed?.dpiIndex ?? 0;
    const wirelessRate = type3HasRate(perf?.type3?.parsed?.wirelessReportRateHz)
      ? Number(perf.type3.parsed.wirelessReportRateHz)
      : null;
    const wiredRate = type3HasRate(perf?.type3?.parsed?.coredReportRateHz)
      ? Number(perf.type3.parsed.coredReportRateHz)
      : null;
    const wiredRateCustom = !!perf?.type3?.parsed?.coredReportRateCustom;
    const wirelessRateCustom = !!perf?.type3?.parsed?.wirelessReportRateCustom;
    const type3 = perf?.type3?.parsed;
    if (type3) {
      setSupportedRates({
        wired:
          type3.coredSupportedReportRates ||
          pollingRateTableToHz(type3.coredRPTTABLE),
        wireless:
          type3.wirelessSupportedReportRates ||
          pollingRateTableToHz(type3.wirelessRPTTABLE),
      });
    }
    const informationRate = info?.parsed?.reportRateHz || null;
    const connType = info?.parsed?.connectType; // 若有：如 wired / wireless
    const isWiredConn =
      connType === 'wired' ||
      connType === 1 ||
      connType === 'cable' ||
      String(connType || '').toLowerCase() === 'usb';

    setDeviceSnap((prevSnap) => {
      // 保留未被设备回报覆盖的另一侧
      let wireless = prevSnap?.wirelessRate || 1000;
      let wired = prevSnap?.wiredRate || 1000;
      if (wirelessRate) wireless = wirelessRate;
      if (wiredRate) wired = wiredRate;
      // type3 某一侧缺失时，才按当前连接类型使用通告信息补充。
      if (informationRate) {
        if (isWiredConn && !wiredRate) wired = informationRate;
        if (!isWiredConn && !wirelessRate) wireless = informationRate;
      }
      const snap = snapshotFromDevice(
        dpiValues,
        colors,
        level,
        wireless,
        wired,
        {
          wireless: prevSnap?.wirelessRateColors,
          wired: prevSnap?.wiredRateColors,
        },
        {
          wired: wiredRateCustom,
          wireless: wirelessRateCustom,
        }
      );
      setSelectedId((id) => {
        if (id === 'device-current' || !id) {
          setDraft(JSON.parse(JSON.stringify(snap)));
          return 'device-current';
        }
        return id;
      });
      // 编辑「当前设备」时刷新 draft，但不要用设备回报把另一侧回报率冲掉
      setDraft((prev) => {
        // 用户动过「当前设备」草稿之后以界面为准，回读不再回灌——
        // 回读比写入慢，拿它覆盖草稿会把正在编辑的 DPI 打回旧值
        if (ownedByUser()) return prev;
        if (!prev || prev.id === 'device-current' || prev.type === 'device') {
          const next = JSON.parse(JSON.stringify(snap));
          // 设备没有回报某一侧时，保留该侧现有选择。
          if (prev) {
            // 用户已切到独立 X/Y 时，设备回读不要把开关拨回联动
            if (prev.independentXY) next.independentXY = true;
            if (!wiredRate && !isWiredConn && Number(prev.wiredRate) > 0) {
              next.wiredRate = Number(prev.wiredRate);
            }
            if (!wirelessRate && isWiredConn && Number(prev.wirelessRate) > 0) {
              next.wirelessRate = Number(prev.wirelessRate);
            }
            if (prev.wiredRateColors) next.wiredRateColors = prev.wiredRateColors;
            if (prev.wirelessRateColors) {
              next.wirelessRateColors = prev.wirelessRateColors;
            }
            if (wiredRate == null) next.wiredRateCustom = !!prev.wiredRateCustom;
            if (wirelessRate == null) {
              next.wirelessRateCustom = !!prev.wirelessRateCustom;
            }
          }
          return next;
        }
        return prev;
      });
      return snap;
    });
  }, [dataStore, deviceInfo, ownedByUser]);

  useEffect(() => {
    if (!dataStore) return;
    const info = dataStore.getData('informationData');
    const perf = dataStore.getData('performanceData');
    const color = dataStore.getData('colorData');
    applyDeviceData(info, perf, color);

    const onInfo = (data) =>
      applyDeviceData(data, dataStore.getData('performanceData'), dataStore.getData('colorData'));
    const onPerf = (data) =>
      applyDeviceData(dataStore.getData('informationData'), data, dataStore.getData('colorData'));
    const onColor = (data) =>
      applyDeviceData(dataStore.getData('informationData'), dataStore.getData('performanceData'), data);
    dataStore.on('informationData', onInfo);
    dataStore.on('performanceData', onPerf);
    dataStore.on('colorData', onColor);
    return () => {
      dataStore.off('informationData', onInfo);
      dataStore.off('performanceData', onPerf);
      dataStore.off('colorData', onColor);
    };
  }, [dataStore, applyDeviceData]);

  const allCustom = customPresets;
  const featuredFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return FEATURED_PRESETS;
    return FEATURED_PRESETS.filter((p) => p.name.toLowerCase().includes(q));
  }, [search]);
  const customFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allCustom;
    return allCustom.filter((p) => p.name.toLowerCase().includes(q));
  }, [search, allCustom]);

  const selectPreset = (preset) => {
    const normalized = normalizePreset(JSON.parse(JSON.stringify(preset)));
    setSelectedId(normalized.id);
    setDraft(normalized);
    setRenaming(false);
    setMenuLevel(null);
    setColorTarget(null);
  };

  const updateDraft = (patch) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev));
  };

  const updateLevel = (index, patch) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const levels = prev.levels.map((lv, i) => (i === index ? { ...lv, ...patch } : lv));
      return { ...prev, levels };
    });
  };

  /** X / Y 是否分开调节 */
  const independentXY = !!(draft ? draft.independentXY : deviceSnap?.independentXY);

  const handleRenameStart = () => {
    if (!draft || isFeatured) return;
    setNameInput(draft.name);
    setRenaming(true);
  };

  const handleRenameCommit = () => {
    if (!draft) return;
    const name = nameInput.trim() || draft.name;
    updateDraft({ name });
    if (isCustom) {
      setCustomPresets((list) =>
        list.map((p) => (p.id === draft.id ? { ...p, name } : p))
      );
    }
    setRenaming(false);
  };

  const uniqueCustomName = (base) => {
    const names = new Set(customPresets.map((p) => p.name));
    let n = base;
    let i = 2;
    while (names.has(n)) {
      n = `${base} ${i++}`;
    }
    return n;
  };

  const withPresetActionLock = (fn) => {
    if (presetActionLockRef.current) return;
    presetActionLockRef.current = true;
    setPresetActionLock(true);
    try {
      fn();
    } finally {
      // 短暂锁定，吞掉同一次连点 / 双击产生的额外触发
      window.setTimeout(() => {
        presetActionLockRef.current = false;
        setPresetActionLock(false);
      }, 600);
    }
  };

  const handleSaveAsCustom = () => {
    withPresetActionLock(() => {
      if (!draft) return;
      const copy = {
        ...JSON.parse(JSON.stringify(draft)),
        id: uid(),
        type: 'custom',
        name: uniqueCustomName(isCustom ? `${draft.name} 副本` : `${draft.name}`),
      };
      setCustomPresets((list) => [copy, ...list]);
      setCustomOpen(true);
      selectPreset(copy);
      toast.success('已保存到自定义预设');
    });
  };

  /** 基于当前设备快照新建空白自定义预设 */
  const handleCreateCustom = (e) => {
    e?.stopPropagation?.();
    withPresetActionLock(() => {
      const base = JSON.parse(JSON.stringify(deviceSnap));
      const created = {
        ...base,
        id: uid(),
        type: 'custom',
        name: uniqueCustomName('自定义预设'),
      };
      setCustomPresets((list) => [created, ...list]);
      setCustomOpen(true);
      selectPreset(created);
      setNameInput(created.name);
      setRenaming(true);
      toast.success('已创建自定义预设');
    });
  };

  const handleDuplicate = () => {
    if (!draft) return;
    if (!isCustom && !isFeatured && !isDevice) return;
    handleSaveAsCustom();
  };

  const handleDuplicatePreset = (preset) => {
    if (!preset) return;
    withPresetActionLock(() => {
      const copy = {
        ...JSON.parse(JSON.stringify(preset)),
        id: uid(),
        type: 'custom',
        name: uniqueCustomName(`${preset.name} 副本`),
      };
      setCustomPresets((list) => [copy, ...list]);
      setCustomOpen(true);
      selectPreset(copy);
      setPresetMenu(null);
      toast.success('已复制自定义预设');
    });
  };

  const handleRenamePreset = (preset) => {
    if (!preset) return;
    selectPreset(preset);
    setNameInput(preset.name);
    setRenaming(true);
    setPresetMenu(null);
  };

  const handleDeletePreset = (preset) => {
    if (!preset) return;
    setPresetMenu(null);
    if (!window.confirm(`删除自定义预设「${preset.name}」？`)) return;
    setCustomPresets((list) => list.filter((p) => p.id !== preset.id));
    if (selectedId === preset.id) selectPreset(DEFAULT_FEATURED);
    toast.success('已删除');
  };

  const handleDelete = () => {
    if (!isCustom || !draft) return;
    handleDeletePreset(draft);
  };

  const handleResetLevel = (index) => {
    if (!draft) return;
    const def = clampDpi(draft.levels[index]?.defaultDpi ?? LEVEL_DEFAULT_DPI[index] ?? 1600);
    if (isDevice) markDirty();
    updateLevel(index, { dpi: def, dpiY: def });
    setMenuLevel(null);
  };

  /** 切换「X/Y 联动 ↔ X/Y 独立」；关闭独立时 Y 跟随 X */
  const handleToggleIndependentXY = () => {
    const next = !independentXY;
    if (isDevice) markDirty();
    const applyLevels = (levels = []) =>
      levels.map((lv) => ({
        ...lv,
        dpi: clampDpi(lv.dpi),
        dpiY: next ? levelDpiY(lv) : clampDpi(lv.dpi),
      }));
    setDraft((prev) =>
      prev ? { ...prev, independentXY: next, levels: applyLevels(prev.levels) } : prev
    );
    setDeviceSnap((prev) =>
      prev && (isDevice || !draft)
        ? { ...prev, independentXY: next, levels: applyLevels(prev.levels) }
        : prev
    );
    if (isCustom && draft) {
      setCustomPresets((list) =>
        list.map((p) =>
          p.id === draft.id
            ? { ...p, independentXY: next, levels: applyLevels(p.levels) }
            : p
        )
      );
    }
    setMenuLevel(null);
  };

  const openColorPicker = (target, currentColor, e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    const rect = e?.currentTarget?.getBoundingClientRect?.();
    const anchor = rect
      ? { top: rect.bottom + 8, left: rect.left, right: rect.right }
      : { top: 120, left: 120, right: 136 };
    setTempColor(Array.isArray(currentColor) ? [...currentColor] : [255, 255, 255]);
    setColorTarget({ ...target, anchor });
    setMenuLevel(null);
  };

  const applyTempColor = async (rgb) => {
    if (!colorTarget || !draft) return;
    const next = [clampByte(rgb[0]), clampByte(rgb[1]), clampByte(rgb[2])];
    setTempColor(next);

    if (colorTarget.type === 'level') {
      updateLevel(colorTarget.index, { color: next });
      if (isCustom) {
        setCustomPresets((list) =>
          list.map((p) => {
            if (p.id !== draft.id) return p;
            const levels = p.levels.map((lv, i) =>
              i === colorTarget.index ? { ...lv, color: next } : lv
            );
            return { ...p, levels };
          })
        );
      }
      // 档位灯色：有协议支持时即时写入
      if (isConnected && sender && dataStore && (isDevice || selectedId === 'device-current')) {
        try {
          const usesLegacyColorPacket =
            !dataStore.isProtocolAtLeast(4, 0) && isLegacyDpiColorModel(deviceInfo);
          let raw = usesLegacyColorPacket ? getDpiColorRaw() : getType1Raw();
          if (!raw) {
            if (usesLegacyColorPacket) {
              await sender.getColorData(1);
            } else {
              await sender.getPerformanceData(1);
            }
            await sender.wait(60);
            raw = usesLegacyColorPacket ? getDpiColorRaw() : getType1Raw();
          }
          if (raw) {
            await sender.setDPIColor(colorTarget.index, next, raw);
            await sender.wait(40);
            if (usesLegacyColorPacket) {
              await sender.getColorData(1);
            } else {
              await sender.getPerformanceData(1);
            }
          }
        } catch (err) {
          console.error(err);
        }
      }
      return;
    }

    if (colorTarget.type === 'rate' && colorTarget.hz != null) {
      const mapKey =
        colorTarget.rateKey === 'wired' ? 'wiredRateColors' : 'wirelessRateColors';
      const hz = Number(colorTarget.hz);
      setDraft((prev) => {
        if (!prev) return prev;
        const map = makeRateColorMap(prev[mapKey]);
        map[hz] = next;
        return { ...prev, [mapKey]: map };
      });
      if (isCustom) {
        setCustomPresets((list) =>
          list.map((p) => {
            if (p.id !== draft.id) return p;
            const map = makeRateColorMap(p[mapKey]);
            map[hz] = next;
            return { ...p, [mapKey]: map };
          })
        );
      }
      // 回报率每档灯色：type3 无独立 RGB 表前先存预设
    }
  };

  const getType1Raw = () => dataStore?.getData('performanceData')?.type1?.raw;
  const getDpiColorRaw = () => dataStore?.getData('colorData')?.raw;
  const getType3Raw = () => dataStore?.getData('performanceData')?.type3?.raw;

  /** 轮询等待 store 中出现 type1/type3 */
  const waitForPerformance = async (getter, tries = 8, gapMs = 40) => {
    for (let i = 0; i < tries; i++) {
      const v = getter();
      if (v && v.length >= 14) return v;
      await sender.wait(gapMs);
    }
    return getter();
  };

  /** 确保 type1 / type3 性能数据就绪；缺失时主动拉取 */
  const ensurePerformanceRaw = async () => {
    let type1 = getType1Raw();
    let type3 = getType3Raw();
    if (type1?.length >= 14 && type3?.length >= 14) {
      return { type1, type3 };
    }

    if (!sender) {
      throw new Error('设备未连接');
    }

    if (!(type1?.length >= 14)) {
      await sender.getPerformanceData(1);
      type1 = await waitForPerformance(getType1Raw);
    }
    if (!(type3?.length >= 14)) {
      await sender.getPerformanceData(3);
      type3 = await waitForPerformance(getType3Raw);
    }

    if (!(type1?.length >= 14) || !(type3?.length >= 14)) {
      throw new Error('获取设备性能数据失败，请重试');
    }
    return { type1, type3 };
  };

  /** 手动应用：预设或当前草稿整体写入设备，带 busy 状态与结果提示 */
  const applyToDevice = async (preset = null) => {
    const targetPreset = preset?.levels ? preset : draft;
    if (!targetPreset) return;
    if (!isConnected || !sender || !dataStore) {
      toast.error('设备未连接');
      return;
    }

    setBusy(true);
    try {
      await writePresetToDevice(targetPreset);
      toast.success('已应用到设备');
    } catch (err) {
      console.error(err);
      toast.error(`应用失败: ${err.message || '写入设备失败'}`);
    } finally {
      setBusy(false);
    }
  };

  const handleSelectLevel = async (index) => {
    updateDraft({ currentLevel: index });
    if (!isConnected || !sender || !dataStore) return;
    try {
      let raw = getType1Raw();
      if (!raw) {
        await sender.getPerformanceData(1);
        await sender.wait(60);
        raw = getType1Raw();
      }
      if (!raw) return;
      await sender.setDPILevel(index, raw);
      await sender.wait(60);
      await sender.getPerformanceData(1);
      await sender.getInformationData();
    } catch (e) {
      console.error(e);
    }
  };

  /** 联动模式下改 X 会同步 Y；独立模式下两轴各改各的 */
  const dpiPatchFor = (axis, value) => {
    const dpi = clampDpi(value);
    if (axis === 'y') return { dpiY: dpi };
    return independentXY ? { dpi } : { dpi, dpiY: dpi };
  };

  const commitLevelDpi = async (index, value, axis = 'x') => {
    const patch = dpiPatchFor(axis, value);
    // 编辑「当前设备」时停手后自动写入；预设编辑仍走手动应用按钮
    if (isDevice) markDirty();
    updateLevel(index, patch);
    if (isCustom && draft) {
      setCustomPresets((list) =>
        list.map((p) => {
          if (p.id !== draft.id) return p;
          const levels = p.levels.map((lv, i) => (i === index ? { ...lv, ...patch } : lv));
          return { ...p, levels };
        })
      );
    }
  };

  const handleRateChange = async (key, hz, custom = false) => {
    const nextHz = Number(hz);
    const customFlag = usesSharedReportRate ? false : !!custom;
    const rateUpdate = usesSharedReportRate
      ? {
          wiredRate: nextHz,
          wirelessRate: nextHz,
          wiredRateCustom: false,
          wirelessRateCustom: false,
        }
      : {
          [key]: nextHz,
          [key === 'wiredRate' ? 'wiredRateCustom' : 'wirelessRateCustom']:
            customFlag,
        };
    updateDraft(rateUpdate);
    // 同步 deviceSnap，避免后续设备回读把另一侧冲掉时丢本地选择
    setDeviceSnap((prev) => (prev ? { ...prev, ...rateUpdate } : prev));
    if (isCustom && draft) {
      setCustomPresets((list) =>
        list.map((p) => (p.id === draft.id ? { ...p, ...rateUpdate } : p))
      );
    }
    if (isConnected && sender && dataStore) {
      try {
        let raw = getType3Raw();
        if (!raw) {
          await sender.getPerformanceData(3);
          await sender.wait(60);
          raw = getType3Raw();
        }
        if (!raw) {
          toast.error('获取设备性能数据失败');
          return;
        }
        await sender.setPollingRates(
          usesSharedReportRate
            ? { wiredRate: nextHz }
            : key === 'wiredRate'
            ? { wiredRate: nextHz, wiredCustom: customFlag }
            : { wirelessRate: nextHz, wirelessCustom: customFlag },
          raw
        );
        await sender.wait(60);
        await sender.getPerformanceData(3);
        await sender.getInformationData();
      } catch (e) {
        console.error(e);
        toast.error(`设置回报率失败: ${e.message}`);
      }
    }
  };

  const activeDraft = draft || deviceSnap;

  const ColorPopover = () => {
    if (!colorTarget) return null;
    const title =
      colorTarget.type === 'rate'
        ? `${colorTarget.rateKey === 'wired' ? '有线' : '无线'} ${colorTarget.hz} Hz 灯光`
        : `档位 ${(colorTarget.index ?? 0) + 1} 灯光`;
    const anchor = colorTarget.anchor || { top: 120, left: 120 };
    // 尽量不贴右边缘裁切
    const popWidth = 220;
    const left = Math.min(
      Math.max(8, anchor.left),
      (typeof window !== 'undefined' ? window.innerWidth : 1200) - popWidth - 8
    );
    const top = Math.min(
      Math.max(8, anchor.top),
      (typeof window !== 'undefined' ? window.innerHeight : 800) - 260
    );
    return (
      <div
        className="sens-color-popover"
        ref={colorPopRef}
        style={{ top, left }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sens-color-popover-title">{title}</div>
        <div className="sens-color-preview-row">
          <span className="sens-color-preview" style={{ background: rgbCss(tempColor) }} />
          <input
            type="color"
            className="sens-color-native"
            value={rgbToHex(tempColor)}
            onChange={(e) => applyTempColor(hexToRgb(e.target.value))}
          />
          <span className="sens-color-rgb">
            RGB({tempColor[0]}, {tempColor[1]}, {tempColor[2]})
          </span>
        </div>
        <div className="sens-color-palette">
          {PALETTE.map((c, i) => (
            <button
              key={i}
              type="button"
              className="sens-palette-swatch"
              style={{ background: rgbCss(c) }}
              title={`RGB(${c[0]},${c[1]},${c[2]})`}
              onClick={() => applyTempColor(c)}
            />
          ))}
        </div>
        <button type="button" className="sens-color-done" onClick={() => setColorTarget(null)}>
          完成
        </button>
      </div>
    );
  };

  const rateColorOf = (rateKey, hz) => {
    const mapKey = rateKey === 'wired' ? 'wiredRateColors' : 'wirelessRateColors';
    const map = activeDraft[mapKey] || makeRateColorMap();
    return map[hz] || map[String(hz)] || DEFAULT_RATE_COLORS[hz] || DEFAULT_COLORS[0];
  };

  const RateBlock = ({ rateKey, label, value, custom }) => {
    const selected = Number(value) || 1000;
    const customActive = !usesSharedReportRate && !!custom;
    const options =
      rateKey === 'wired' ? supportedRates.wired : supportedRates.wireless;
    const rateKeyName = rateKey === 'wired' ? 'wiredRate' : 'wirelessRate';
    const commitCustom = (raw) => {
      handleRateChange(
        rateKeyName,
        clampCustomReportRateHz(raw, selected),
        true
      );
    };
    return (
      <div className="sens-rate-field">
        <div className="sens-rate-subhead">{label}</div>
        <div className="sens-rate-levels" role="listbox" aria-label={label}>
          {options.map((hz) => {
            const active = !customActive && selected === hz;
            const color = rateColorOf(rateKey, hz);
            return (
              <div
                key={hz}
                role="option"
                aria-selected={active}
                className={`sens-rate-level-row ${active ? 'active' : ''}`}
                onClick={() => handleRateChange(rateKeyName, hz, false)}
              >
                <span className={`sens-rate-radio ${active ? 'on' : ''}`} aria-hidden />
                <div className="sens-color-swatch-wrap" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="sens-color-swatch"
                    style={{ background: rgbCss(color) }}
                    title={`${hz} Hz 灯光颜色`}
                    onClick={(e) =>
                      openColorPicker({ type: 'rate', rateKey, hz }, color, e)
                    }
                  />
                </div>
                <span className="sens-rate-hz">{hz} Hz</span>
                {active && <span className="sens-rate-current">当前</span>}
              </div>
            );
          })}
          {!usesSharedReportRate && (
            <div
              role="option"
              aria-selected={customActive}
              className={`sens-rate-level-row sens-rate-custom ${
                customActive ? 'active' : ''
              }`}
              onClick={() => commitCustom(selected)}
            >
              <span
                className={`sens-rate-radio ${customActive ? 'on' : ''}`}
                aria-hidden
              />
              <span className="sens-rate-hz">自定义</span>
              <label className="sens-rate-custom-val">
                <input
                  type="number"
                  aria-label={`${label}自定义`}
                  min={CUSTOM_REPORT_RATE_RANGE.min}
                  max={CUSTOM_REPORT_RATE_RANGE.max}
                  key={`${rateKey}-${customActive ? selected : 'idle'}`}
                  defaultValue={customActive ? selected : ''}
                  placeholder={`${CUSTOM_REPORT_RATE_RANGE.min}`}
                  onClick={(event) => event.stopPropagation()}
                  onBlur={(event) => commitCustom(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.currentTarget.blur();
                    }
                  }}
                />
                <span>Hz</span>
              </label>
              {customActive && <span className="sens-rate-current">当前</span>}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (theme === 'theme2') {
    const activeIndex = Math.min(
      Math.max(0, activeDraft.currentLevel ?? 0),
      Math.max(0, activeDraft.levels.length - 1)
    );
    const activeLevel = activeDraft.levels[activeIndex] || activeDraft.levels[0];
    // 独立模式下滑条只作用于当前选中的轴
    const editingAxis = independentXY ? theme2Axis : 'x';
    const activeDpi =
      (editingAxis === 'y' ? levelDpiY(activeLevel) : activeLevel?.dpi) || 1600;
    const sliderPosition = dpiToTheme2Slider(activeDpi);
    const sliderFill = `${(sliderPosition / THEME2_SLIDER_MAX) * 100}%`;
    const sliderLevelColor = rgbCss(
      activeLevel?.color || DEFAULT_COLORS[activeIndex] || DEFAULT_COLORS[0]
    );
    const mouseImage = deviceInfo?.image;
    const changeActiveDpi = (nextValue) => {
      updateLevel(activeIndex, dpiPatchFor(editingAxis, nextValue));
    };
    return (
      <div className="theme2-dpi-page">
        {colorTarget && <ColorPopover />}
        <section className="theme2-device-stage" aria-label="鼠标 DPI 按键示意">
          <div className="theme2-mouse-visual theme2-mouse-front">
            {mouseImage ? <img src={mouseImage} alt={deviceInfo?.productName || '鼠标正面'} /> : <div className="theme2-mouse-placeholder" />}
          </div>
        </section>

        <section className={`theme2-dpi-workspace ${independentXY ? 'xy-independent' : ''}`}>
          <div className="theme2-dpi-level-card">
            <div className="theme2-card-heading">
              <h2>DPI档位</h2>
            </div>
            <div className="theme2-level-grid">
              {activeDraft.levels.map((level, index) => (
                <div
                  key={`${index}-${level.defaultDpi}`}
                  className={`theme2-level-chip ${index === activeIndex ? 'active' : ''}`}
                >
                  <button
                    type="button"
                    className="theme2-level-color-line"
                    style={{
                      background: rgbCss(
                        level.color || DEFAULT_COLORS[index] || DEFAULT_COLORS[0]
                      ),
                    }}
                    title={`修改 DPI 档位 ${index + 1} 颜色`}
                    aria-label={`修改 DPI 档位 ${index + 1} 颜色`}
                    onClick={(event) =>
                      openColorPicker(
                        { type: 'level', index },
                        level.color || DEFAULT_COLORS[index] || DEFAULT_COLORS[0],
                        event
                      )
                    }
                  />
                  <button
                    type="button"
                    className="theme2-level-value"
                    onClick={() => handleSelectLevel(index)}
                  >
                    {independentXY ? `${level.dpi} / ${levelDpiY(level)}` : level.dpi}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="theme2-dpi-control-card">
            <div className="theme2-card-heading">
              <h2>DPI设置</h2>
              <div className="theme2-heading-actions">
                {independentXY && (
                  <div className="theme2-axis-tabs" role="group" aria-label="选择调节的轴">
                    {[
                      { key: 'x', label: 'X 轴' },
                      { key: 'y', label: 'Y 轴' },
                    ].map((axis) => (
                      <button
                        key={axis.key}
                        type="button"
                        className={editingAxis === axis.key ? 'active' : ''}
                        aria-pressed={editingAxis === axis.key}
                        onClick={() => setTheme2Axis(axis.key)}
                      >
                        {axis.label}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className={`theme2-color-button theme2-axis-button ${
                    independentXY ? 'on' : ''
                  }`}
                  title={
                    independentXY
                      ? '切回 X / Y 联动：Y 轴 DPI 跟随 X 轴'
                      : '切到 X / Y 独立：分别设置横向、纵向 DPI'
                  }
                  aria-pressed={independentXY}
                  onClick={() => {
                    setTheme2Axis('x');
                    handleToggleIndependentXY();
                  }}
                >
                  {independentXY ? 'X / Y 独立' : 'X / Y 联动'}
                </button>
                <button
                  type="button"
                  className="theme2-color-button"
                  title="修改当前 DPI 档位颜色"
                  onClick={(event) =>
                    openColorPicker(
                      { type: 'level', index: activeIndex },
                      activeLevel?.color || DEFAULT_COLORS[activeIndex] || DEFAULT_COLORS[0],
                      event
                    )
                  }
                >
                  <span
                    className="theme2-color-button-mark"
                    style={{ background: sliderLevelColor }}
                    aria-hidden
                  />
                  修改档位颜色
                </button>
              </div>
            </div>
            <div className="theme2-slider-area">
              <output className="theme2-dpi-output" style={{ left: sliderFill }}>
                {independentXY ? `${editingAxis.toUpperCase()} ${activeDpi}` : activeDpi}
              </output>
              <input
                type="range"
                className="theme2-dpi-slider"
                min="0"
                max={THEME2_SLIDER_MAX}
                step="1"
                value={sliderPosition}
                aria-valuetext={`${activeDpi} DPI`}
                style={{ '--fill': sliderFill, '--level-color': sliderLevelColor }}
                onChange={(event) => changeActiveDpi(theme2SliderToDpi(event.target.value))}
                onMouseUp={(event) =>
                  commitLevelDpi(activeIndex, theme2SliderToDpi(event.currentTarget.value), editingAxis)
                }
                onTouchEnd={(event) =>
                  commitLevelDpi(activeIndex, theme2SliderToDpi(event.currentTarget.value), editingAxis)
                }
              />
              <div className="theme2-slider-labels">
                {THEME2_DPI_SHORTCUTS.map((dpi) => (
                  <button
                    type="button"
                    key={dpi}
                    className={activeDpi === dpi ? 'active' : ''}
                    style={{ '--position': `${(dpiToTheme2Slider(dpi) / THEME2_SLIDER_MAX) * 100}%` }}
                    onClick={() => {
                      changeActiveDpi(dpi);
                      commitLevelDpi(activeIndex, dpi, editingAxis);
                    }}
                  >
                    {dpi}
                  </button>
                ))}
              </div>
            </div>
            <div className="theme2-control-footer">
              {isDevice ? (
                <span
                  className={`sens-save-status is-${saveStatus}`}
                  role="status"
                  aria-live="polite"
                >
                  {isConnected
                    ? AUTO_APPLY_STATUS_TEXT[saveStatus]
                    : '设备未连接'}
                </span>
              ) : (
                <button type="button" className="theme2-apply-button" disabled={busy || !isConnected} onClick={() => applyToDevice()}>
                  {busy ? '应用中…' : '应用到设备'}
                </button>
              )}
              <div className="theme2-dpi-stepper">
                <button type="button" onClick={() => changeActiveDpi(activeDpi - 50)} aria-label="降低 DPI">‹</button>
                <input
                  type="number"
                  min="100"
                  max="30000"
                  step="50"
                  value={activeDpi}
                  onChange={(event) => changeActiveDpi(event.target.value)}
                  onBlur={(event) => commitLevelDpi(activeIndex, event.target.value, editingAxis)}
                />
                <button type="button" onClick={() => changeActiveDpi(activeDpi + 50)} aria-label="提高 DPI">›</button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={`sensitivity ${isPresetListRight ? 'list-right' : ''}`}>
      {/* fixed 弹层：避免被 overflow 裁切 */}
      {colorTarget && <ColorPopover />}
      {presetMenu && (
        <div
          ref={presetMenuRef}
          className="sens-preset-action-menu"
          style={{ top: presetMenu.top, left: presetMenu.left }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button type="button" onClick={() => handleRenamePreset(presetMenu.preset)}>
            重命名
          </button>
          <button
            type="button"
            disabled={presetActionLock}
            onClick={() => handleDuplicatePreset(presetMenu.preset)}
          >
            复制
          </button>
          <button
            type="button"
            disabled={busy || !isConnected}
            onClick={async () => {
              const preset = presetMenu.preset;
              selectPreset(preset);
              setPresetMenu(null);
              await applyToDevice(preset);
            }}
          >
            {busy ? '应用中…' : '应用到设备'}
          </button>
          <button
            type="button"
            className="danger"
            onClick={() => handleDeletePreset(presetMenu.preset)}
          >
            删除
          </button>
        </div>
      )}
      {/* 左侧预设列表 */}
      <aside className="sens-list">
        <div className="sens-list-header">
          <h2>灵敏度</h2>
          <button
            type="button"
            className="sens-layout-switch"
            aria-label={
              isPresetListRight
                ? '将灵敏度预设列表移到左侧'
                : '将灵敏度预设列表移到右侧'
            }
            data-tooltip={
              isPresetListRight
                ? '将灵敏度预设列表移到左侧'
                : '将灵敏度预设列表移到右侧'
            }
            onClick={() => {
              const nextIsRight = !isPresetListRight;
              setIsPresetListRight(nextIsRight);
              writePanelOrder(
                SENSITIVITY_PANEL_ORDER_KEY,
                nextIsRight ? 'list-right' : 'list-left'
              );
            }}
          >
            <ChevronIcon direction={isPresetListRight ? 'left' : 'right'} />
          </button>
        </div>

        <div className="sens-search">
          <span className="sens-search-icon" aria-hidden />
          <input
            type="search"
            placeholder="搜索预设"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="sens-section">
          <div className="sens-section-body">
            <button
              type="button"
              className={`sens-preset-item ${selectedId === deviceSnap.id ? 'active' : ''}`}
              onClick={() => selectPreset(deviceSnap)}
            >
              <span className="sens-preset-name">板载灵敏度</span>
            </button>
          </div>
        </div>

        <div className="sens-section">
          <button
            type="button"
            className="sens-section-toggle"
            onClick={() => setFeaturedOpen((v) => !v)}
          >
            <span className={`sens-chevron ${featuredOpen ? 'open' : ''}`}>›</span>
            <span>精选预设</span>
            <span className="sens-section-count">{featuredFiltered.length}</span>
          </button>
          {featuredOpen && (
            <div className="sens-section-body">
              {featuredFiltered.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`sens-preset-item ${selectedId === p.id ? 'active' : ''}`}
                  onClick={() => selectPreset(p)}
                >
                  <span className="sens-preset-name">{p.name}</span>
                </button>
              ))}
              {featuredFiltered.length === 0 && (
                <div className="sens-empty">无匹配预设</div>
              )}
            </div>
          )}
        </div>

        <div className="sens-section">
          <div className="sens-section-header">
            <button
              type="button"
              className="sens-section-toggle"
              onClick={() => setCustomOpen((v) => !v)}
            >
              <span className={`sens-chevron ${customOpen ? 'open' : ''}`}>›</span>
              <span>自定义预设</span>
              <span className="sens-section-count">{customFiltered.length}</span>
            </button>
            <button
              type="button"
              className="sens-section-add"
              title="创建自定义预设"
              aria-label="创建自定义预设"
              disabled={presetActionLock}
              onClick={handleCreateCustom}
            >
              +
            </button>
          </div>
          {customOpen && (
            <div className="sens-section-body">
              {customFiltered.map((p) => (
                <div
                  key={p.id}
                  className={`sens-custom-preset-row ${selectedId === p.id ? 'active' : ''}`}
                >
                  <button
                    type="button"
                    className={`sens-preset-item ${selectedId === p.id ? 'active' : ''}`}
                    onClick={() => selectPreset(p)}
                  >
                    <span className="sens-preset-name">{p.name}</span>
                  </button>
                  <button
                    type="button"
                    className={`sens-preset-more ${presetMenu?.preset?.id === p.id ? 'open' : ''}`}
                    aria-label={`管理预设 ${p.name}`}
                    title="更多操作"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const menuWidth = 168;
                      const menuHeight = 164;
                      const left = Math.min(
                        Math.max(8, rect.right - menuWidth),
                        window.innerWidth - menuWidth - 8
                      );
                      const top =
                        rect.bottom + menuHeight + 8 <= window.innerHeight
                          ? rect.bottom + 4
                          : Math.max(8, rect.top - menuHeight - 4);
                      setPresetMenu((current) =>
                        current?.preset?.id === p.id ? null : { preset: p, top, left }
                      );
                    }}
                  >
                    ⋮
                  </button>
                </div>
              ))}
              {customFiltered.length === 0 && (
                <button
                  type="button"
                  className="sens-empty-create"
                  disabled={presetActionLock}
                  onClick={handleCreateCustom}
                >
                  + 创建自定义预设
                </button>
              )}
            </div>
          )}
        </div>
      </aside>

      {/* 右侧编辑区 */}
      <section className="sens-editor">
        <div className="sens-editor-top">
          {renaming ? (
            <input
              ref={nameRef}
              className="sens-title-input"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={handleRenameCommit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameCommit();
                if (e.key === 'Escape') setRenaming(false);
              }}
            />
          ) : (
            <h1 className="sens-title">{activeDraft.name}</h1>
          )}

          <div className="sens-toolbar">
            <button
              type="button"
              className="sens-tool-btn"
              title="重命名"
              disabled={isFeatured || isDevice}
              onClick={handleRenameStart}
            >
              <span className="icon-rename" />
              重命名
            </button>
            {!isCustom && (
              <button
                type="button"
                className="sens-tool-btn"
                title="保存为自定义预设"
                disabled={!draft || presetActionLock}
                onClick={handleSaveAsCustom}
              >
                <span className="icon-download" />
                {presetActionLock ? '处理中…' : '保存为自定义预设'}
              </button>
            )}
            <button
              type="button"
              className="sens-tool-btn"
              title="复制"
              disabled={!draft || presetActionLock}
              onClick={handleDuplicate}
            >
              <span className="icon-copy" />
              复制
            </button>
            <button
              type="button"
              className="sens-tool-btn danger"
              title="删除"
              disabled={!isCustom}
              onClick={handleDelete}
            >
              <span className="icon-trash" />
              删除
            </button>
            {isDevice ? (
              <span
                className={`sens-save-status is-${saveStatus}`}
                role="status"
                aria-live="polite"
              >
                {isConnected
                  ? AUTO_APPLY_STATUS_TEXT[saveStatus]
                  : '设备未连接'}
              </span>
            ) : (
              <button
                type="button"
                className="sens-tool-btn primary"
                disabled={busy || !isConnected}
                onClick={() => applyToDevice()}
              >
                {busy ? '应用中…' : '应用到设备'}
              </button>
            )}
          </div>
        </div>

        <div className="sens-editor-body">
          {/* 左侧：档位列表 */}
          <div className="sens-levels">
            <div className="sens-pane-head">
              <div className="sens-pane-title">档位</div>
              <button
                type="button"
                className={`sens-axis-toggle ${independentXY ? 'on' : ''}`}
                title={
                  independentXY
                    ? '切回 X / Y 联动：Y 轴 DPI 跟随 X 轴'
                    : '切到 X / Y 独立：分别设置横向、纵向 DPI'
                }
                aria-pressed={independentXY}
                onClick={handleToggleIndependentXY}
              >
                <span className="sens-axis-toggle-dot" aria-hidden />
                {independentXY ? 'X / Y 独立' : 'X / Y 联动'}
              </button>
            </div>
            <div className="sens-level-list">
              {activeDraft.levels.map((lv, index) => {
                const active = (activeDraft.currentLevel ?? 0) === index;
                const axes = independentXY
                  ? [
                      { key: 'x', label: 'X', value: clampDpi(lv.dpi) },
                      { key: 'y', label: 'Y', value: levelDpiY(lv) },
                    ]
                  : [{ key: 'x', label: '', value: clampDpi(lv.dpi) }];
                return (
                  <div
                    key={index}
                    className={`sens-level-row ${active ? 'active' : ''} ${
                      independentXY ? 'xy' : ''
                    }`}
                    onClick={() => handleSelectLevel(index)}
                  >
                    <div className="sens-color-swatch-wrap" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="sens-color-swatch"
                        style={{ background: rgbCss(lv.color) }}
                        title="档位灯光颜色"
                        onClick={(e) => openColorPicker({ type: 'level', index }, lv.color, e)}
                      />
                    </div>
                    <span className="sens-level-index">{index + 1}</span>
                    <div className="sens-level-axes">
                      {axes.map((axis) => (
                        <div className="sens-level-axis" key={axis.key}>
                          {axis.label && (
                            <span className="sens-axis-label">{axis.label}</span>
                          )}
                          <div
                            className="sens-level-slider-wrap"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="range"
                              min={100}
                              max={30000}
                              step={50}
                              value={axis.value}
                              aria-label={`档位 ${index + 1} ${axis.label || ''} DPI`}
                              onChange={(e) =>
                                updateLevel(index, dpiPatchFor(axis.key, e.target.value))
                              }
                              onMouseUp={(e) => commitLevelDpi(index, e.target.value, axis.key)}
                              onTouchEnd={(e) => commitLevelDpi(index, e.target.value, axis.key)}
                              className="sens-level-slider"
                              style={{
                                '--fill': `${((axis.value - 100) / (30000 - 100)) * 100}%`,
                                '--track': SLIDER_TRACK,
                              }}
                            />
                          </div>
                          <input
                            type="number"
                            className="sens-level-input"
                            min={100}
                            max={30000}
                            step={50}
                            value={axis.value}
                            aria-label={`档位 ${index + 1} ${axis.label || ''} DPI 数值`}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              updateLevel(index, dpiPatchFor(axis.key, e.target.value))
                            }
                            onBlur={(e) => commitLevelDpi(index, e.target.value, axis.key)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur();
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <div
                      className="sens-level-more-wrap"
                      ref={menuLevel === index ? menuRef : null}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="sens-level-more"
                        aria-label="更多"
                        onClick={() => setMenuLevel(menuLevel === index ? null : index)}
                      >
                        ⋮
                      </button>
                      {menuLevel === index && (
                        <div className="sens-level-menu">
                          <button type="button" onClick={() => handleResetLevel(index)}>
                            重置当前档位默认 DPI
                          </button>
                          {independentXY && (
                            <button
                              type="button"
                              onClick={() => {
                                commitLevelDpi(index, lv.dpi, 'y');
                                setMenuLevel(null);
                              }}
                            >
                              Y 轴跟随 X 轴
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* 右侧：回报率 */}
          <div className="sens-rates">
            <div className="sens-pane-title">回报率</div>
            <div className="sens-rate-stack">
              <p className="sens-rate-hint">
                {usesSharedReportRate
                  ? '当前协议使用同一个回报率设置，同时作用于有线和无线。'
                  : '有线 / 无线回报率互相独立。点击档位用协议表，或点自定义填 Hz。'}
              </p>
              {usesSharedReportRate ? (
                <RateBlock
                  rateKey="wired"
                  label="有线 / 无线回报率"
                  value={activeDraft.wiredRate || activeDraft.wirelessRate || 1000}
                />
              ) : (
                <>
                  <RateBlock
                    rateKey="wired"
                    label="有线回报率"
                    value={activeDraft.wiredRate || 1000}
                    custom={!!activeDraft.wiredRateCustom}
                  />
                  <RateBlock
                    rateKey="wireless"
                    label="无线回报率"
                    value={activeDraft.wirelessRate || 1000}
                    custom={!!activeDraft.wirelessRateCustom}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
